import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, CheckCircle, Warning, Eye, Cursor } from "@phosphor-icons/react";
import { formatTorontoTimestamp } from "../lib/timeDisplay";

/**
 * Reusable "send email" modal used by both the price-list and invoice flows.
 *
 * Props:
 *   kind            "invoice" | "price_list" — drives copy only
 *   customer        the customer object (for the "to" preview)
 *   recipient       the customer's saved billing email (we don't allow override
 *                   per the explicit operator preference: "saved only")
 *   defaultSubject  pre-filled subject
 *   defaultBody     pre-filled plain-text body
 *   onSend          async (overrides) => server response. overrides shape:
 *                   { subject, body, cc }
 *   onClose         () => void
 *   history         optional list of prior email_events to show below the form
 */
export default function EmailSendModal({
  kind,
  customer,
  recipient,
  defaultSubject = "",
  defaultBody = "",
  onSend,
  onClose,
  history = [],
  extraFields = null,
}) {
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  // CC default: CHECKED for invoices (R2), UNCHECKED for price lists. Admin may toggle.
  const [cc, setCc] = useState(kind === "invoice");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // {to, message_id, event_id}

  const noEmail = !recipient;
  const heading = kind === "invoice" ? "Email invoice" : "Email price list";
  const successCopy = kind === "invoice" ? "Invoice emailed" : "Price list emailed";

  const inFlight = useRef(false);
  const send = async () => {
    if (noEmail || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await onSend({ subject, body, cc });
      if (res?.mock) toast.warning(`MOCK — not delivered. Live email is disabled for this send (${res.to}).`);
      else toast.success(`${successCopy} → ${res.to}`);
      setSent(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
      inFlight.current = false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white w-full max-w-2xl border border-zinc-200 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="gf-card-header">
          <h3>{heading}</h3>
        </div>

        <div className="gf-card-body space-y-3 text-sm overflow-y-auto">
          {noEmail ? (
            <div
              className="p-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs"
              data-testid="email-modal-no-recipient"
            >
              <Warning size={14} className="inline -mt-0.5 mr-1" />
              <b>{customer?.company_name || "This customer"}</b> doesn't have a billing email
              on file. Add one in <i>Customer profile → Contact</i> before sending.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="overline">TO</span>
                  <div className="font-mono mt-1" data-testid="email-modal-to">{recipient}</div>
                </div>
                <div>
                  <span className="overline">FROM</span>
                  <div className="font-mono mt-1">Get Fuel Card &lt;noreply@getfuelcard.com&gt;</div>
                </div>
              </div>

              <div>
                <div className="overline mb-1">Subject</div>
                <input
                  className="gf-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="email-modal-subject"
                />
              </div>

              {extraFields && (
                <div data-testid="email-modal-extra-fields">
                  {extraFields}
                </div>
              )}

              <div>
                <div className="overline mb-1">Message</div>
                <textarea
                  className="gf-input"
                  rows={9}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="email-modal-body"
                />
                <div className="text-[11px] text-zinc-500 mt-1">
                  The {kind === "invoice" ? "invoice PDF" : "price-list PDF"} will be attached
                  automatically. Customer's email cannot be changed here — update their
                  Customer profile to send to a different address.
                </div>
              </div>

              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={cc}
                  onChange={(e) => setCc(e.target.checked)}
                  data-testid="email-modal-cc"
                />
                CC <span className="font-mono">hello@getfuelcard.com</span>
                <span className="text-zinc-500">— keeps a copy in your own inbox</span>
              </label>
            </>
          )}

          {sent?.mock && (
            <div
              className="p-2 border border-amber-300 bg-amber-50 text-amber-900 text-xs"
              data-testid="email-modal-mock-banner"
            >
              <Warning size={14} className="inline -mt-0.5 mr-1" />
              MOCK — not delivered. Nothing was sent to the customer; this attempt is recorded as MOCK_SENT.
            </div>
          )}
          {sent && !sent.mock && (
            <div
              className="p-2 border border-green-300 bg-green-50 text-green-900 text-xs"
              data-testid="email-modal-sent-banner"
            >
              <CheckCircle size={14} className="inline -mt-0.5 mr-1" />
              Accepted by Mailgun for delivery. Id <span className="font-mono">{sent.message_id?.slice(0, 28)}…</span>
              {sent.cc?.length > 0 && <> · CC {sent.cc.join(", ")}</>}
              <div className="text-[10px] text-green-800/80 mt-0.5">
                Delivery / open status updates will appear in the history below as Mailgun
                fires the webhook.
              </div>
            </div>
          )}

          {history && history.length > 0 && (
            <div className="border-t border-zinc-200 pt-3">
              <div className="overline mb-2">Send history (last 10)</div>
              <ul className="text-xs space-y-1 font-mono" data-testid="email-modal-history">
                {history.map((h) => (
                  <li key={h.event_id} className="flex items-center gap-2">
                    <StatusPill status={h.status} />
                    <span className="text-zinc-500" title={`UTC: ${h.sent_at}`}>{formatTorontoTimestamp(h.sent_at)} ET</span>
                    {h.source && <span className={`px-1 rounded border text-[10px] ${h.source === "bulk" ? "border-sky-300 text-sky-700" : "border-zinc-300 text-zinc-600"}`} data-testid={`email-history-source-${h.event_id}`}>{h.source.toUpperCase()}</span>}
                    {h.report_date_from && <span className="text-zinc-500">for {h.report_date_from}{h.report_date_to && h.report_date_to !== h.report_date_from ? ` → ${h.report_date_to}` : ""}</span>}
                    <span className="truncate flex-1">{h.to?.[0]}</span>
                    {h.opened_at && <Eye size={11} className="text-blue-600" />}
                    {h.clicked_at && <Cursor size={11} className="text-blue-700" />}
                    {h.error && <span className="text-red-700 truncate" title={h.error}>{h.error}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button
            className="gf-btn gf-btn-outline"
            onClick={onClose}
            disabled={busy}
            data-testid="email-modal-close"
          >
            {sent ? "Close" : "Cancel"}
          </button>
          {!sent && !noEmail && (
            <button
              className="gf-btn gf-btn-primary"
              onClick={send}
              disabled={busy || !subject.trim()}
              data-testid="email-modal-send"
            >
              <EnvelopeSimple size={14} /> {busy ? "Sending…" : "Send"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    sent: "bg-zinc-100 text-zinc-700 border-zinc-300",
    delivered: "bg-green-100 text-green-800 border-green-300",
    opened: "bg-blue-100 text-blue-800 border-blue-300",
    clicked: "bg-blue-100 text-blue-900 border-blue-400",
    bounced: "bg-red-100 text-red-800 border-red-300",
    complained: "bg-red-100 text-red-900 border-red-400",
    failed: "bg-red-100 text-red-800 border-red-300",
    mock_sent: "bg-amber-50 text-amber-800 border-amber-300",
    sending: "bg-sky-50 text-sky-800 border-sky-300",
    skipped: "bg-zinc-50 text-zinc-600 border-zinc-300",
    needs_review: "bg-orange-100 text-orange-900 border-orange-400",
    accepted_admin_confirmed: "bg-zinc-100 text-zinc-700 border-zinc-400",
    not_accepted_admin_confirmed: "bg-red-50 text-red-800 border-red-300",
  };
  const label = (status || "—").toUpperCase();
  return (
    <span
      className={`px-1.5 py-0 text-[10px] tracking-wider border ${styles[status] || styles.sent}`}
      style={{ fontFamily: "inherit" }}
    >
      {label}
    </span>
  );
}
